/**
 * IndexedDB wrapper per storage offline della PWA Letturisti
 * Usa la libreria `idb` per un'API Promise-based
 */
import { openDB } from 'idb';

const DB_NAME = 'gpl-letture-pwa';
const DB_VERSION = 3;

let dbPromise = null;

function getDb() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion) {
        if (oldVersion < 1) {
          // Store impianti
          if (!db.objectStoreNames.contains('plants')) {
            db.createObjectStore('plants', { keyPath: 'id' });
          }
          // Store contatori
          if (!db.objectStoreNames.contains('meters')) {
            const meterStore = db.createObjectStore('meters', { keyPath: 'id' });
            meterStore.createIndex('by_plant', 'plant_id');
          }
          // Store storico letture (cache)
          if (!db.objectStoreNames.contains('readingHistory')) {
            const histStore = db.createObjectStore('readingHistory', { keyPath: 'id' });
            histStore.createIndex('by_meter', 'meter_id');
          }
          // Store letture pendenti (da sincronizzare)
          if (!db.objectStoreNames.contains('pendingReadings')) {
            const pendStore = db.createObjectStore('pendingReadings', { keyPath: 'localId', autoIncrement: true });
            pendStore.createIndex('by_synced', 'synced');
            pendStore.createIndex('by_plant', 'plant_id');
          }
          // Store note intervento (cache)
          if (!db.objectStoreNames.contains('interventionNotes')) {
            const noteStore = db.createObjectStore('interventionNotes', { keyPath: 'id' });
            noteStore.createIndex('by_plant', 'plant_id');
          }
          // Store metadata sync
          if (!db.objectStoreNames.contains('syncMeta')) {
            db.createObjectStore('syncMeta', { keyPath: 'key' });
          }
        }
        // V2: draft per impianto (auto-save)
        if (oldVersion < 2) {
          if (!db.objectStoreNames.contains('plantDrafts')) {
            db.createObjectStore('plantDrafts', { keyPath: 'plantId' });
          }
        }
        // V3: foto pendenti offline
        if (oldVersion < 3) {
          if (!db.objectStoreNames.contains('pendingPhotos')) {
            const photoStore = db.createObjectStore('pendingPhotos', { keyPath: 'localId', autoIncrement: true });
            photoStore.createIndex('by_synced', 'synced');
            photoStore.createIndex('by_plant', 'plant_id');
          }
        }
      }
    });
  }
  return dbPromise;
}

// ============================================
// IMPIANTI
// ============================================

export async function savePlants(plants) {
  const db = await getDb();
  const tx = db.transaction('plants', 'readwrite');
  await Promise.all(plants.map(p => tx.store.put(p)));
  await tx.done;
}

export async function getPlants() {
  const db = await getDb();
  return db.getAll('plants');
}

export async function getPlant(id) {
  const db = await getDb();
  return db.get('plants', id);
}

// ============================================
// CONTATORI
// ============================================

export async function saveMeters(meters) {
  const db = await getDb();
  const tx = db.transaction('meters', 'readwrite');
  await Promise.all(meters.map(m => tx.store.put(m)));
  await tx.done;
}

export async function getMetersByPlant(plantId) {
  const db = await getDb();
  return db.getAllFromIndex('meters', 'by_plant', plantId);
}

// ============================================
// STORICO LETTURE (cache)
// ============================================

export async function saveReadingHistory(readingsByMeter) {
  const db = await getDb();
  const tx = db.transaction('readingHistory', 'readwrite');
  // Salva tutte le letture flat
  for (const [meterId, readings] of Object.entries(readingsByMeter)) {
    for (const r of readings) {
      await tx.store.put({ ...r, meter_id: parseInt(meterId) });
    }
  }
  await tx.done;
}

export async function getReadingHistoryByMeter(meterId) {
  const db = await getDb();
  return db.getAllFromIndex('readingHistory', 'by_meter', meterId);
}

// ============================================
// LETTURE PENDENTI (offline)
// ============================================

export async function savePendingReading(batch) {
  const db = await getDb();
  const record = {
    ...batch,
    synced: 0,
    created_at: new Date().toISOString()
  };
  const id = await db.add('pendingReadings', record);
  return { ...record, localId: id };
}

export async function getPendingReadings() {
  const db = await getDb();
  return db.getAllFromIndex('pendingReadings', 'by_synced', 0);
}

export async function getAllPendingReadings() {
  const db = await getDb();
  return db.getAll('pendingReadings');
}

export async function markPendingAsSynced(localId) {
  const db = await getDb();
  const record = await db.get('pendingReadings', localId);
  if (record) {
    record.synced = 1;
    record.synced_at = new Date().toISOString();
    await db.put('pendingReadings', record);
  }
}

export async function deletePendingReading(localId) {
  const db = await getDb();
  await db.delete('pendingReadings', localId);
}

export async function clearSyncedReadings() {
  const db = await getDb();
  const all = await db.getAll('pendingReadings');
  const tx = db.transaction('pendingReadings', 'readwrite');
  for (const r of all) {
    if (r.synced) await tx.store.delete(r.localId);
  }
  await tx.done;
}

// ============================================
// NOTE INTERVENTO (cache)
// ============================================

export async function saveNotes(notes) {
  const db = await getDb();
  const tx = db.transaction('interventionNotes', 'readwrite');
  await Promise.all(notes.map(n => tx.store.put(n)));
  await tx.done;
}

export async function getNotesByPlant(plantId) {
  const db = await getDb();
  return db.getAllFromIndex('interventionNotes', 'by_plant', plantId);
}

// ============================================
// DRAFT IMPIANTO (auto-save)
// ============================================

export async function saveDraft(plantId, draftData) {
  const db = await getDb();
  await db.put('plantDrafts', {
    plantId,
    ...draftData,
    updated_at: new Date().toISOString()
  });
}

export async function getDraft(plantId) {
  const db = await getDb();
  return db.get('plantDrafts', plantId);
}

export async function deleteDraft(plantId) {
  const db = await getDb();
  try {
    await db.delete('plantDrafts', plantId);
  } catch (_) {}
}

// ============================================
// SYNC METADATA
// ============================================

export async function setSyncMeta(key, value) {
  const db = await getDb();
  await db.put('syncMeta', { key, value, updatedAt: new Date().toISOString() });
}

export async function getSyncMeta(key) {
  const db = await getDb();
  const record = await db.get('syncMeta', key);
  return record?.value || null;
}

// ============================================
// BULK SYNC (dal server)
// ============================================

export async function syncFromServer(data) {
  const { plants, meters, readingsByMeter, notes } = data;

  if (plants) await savePlants(plants);
  if (meters) await saveMeters(meters);
  if (readingsByMeter) await saveReadingHistory(readingsByMeter);
  if (notes) await saveNotes(notes);
  await setSyncMeta('lastSync', data.syncedAt || new Date().toISOString());
}

export async function getLastSyncTime() {
  return getSyncMeta('lastSync');
}

// ============================================
// CLEAR ALL
// ============================================

export async function clearAllData() {
  const db = await getDb();
  const storeNames = ['plants', 'meters', 'readingHistory', 'pendingReadings', 'interventionNotes', 'syncMeta', 'plantDrafts', 'pendingPhotos'];
  for (const name of storeNames) {
    if (db.objectStoreNames.contains(name)) {
      const tx = db.transaction(name, 'readwrite');
      await tx.store.clear();
      await tx.done;
    }
  }
}

// ============================================
// FOTO PENDENTI (offline)
// ============================================

export async function savePendingPhoto(photoRecord) {
  const db = await getDb();
  const record = {
    ...photoRecord,
    synced: 0,
    created_at: new Date().toISOString()
  };
  const id = await db.add('pendingPhotos', record);
  return { ...record, localId: id };
}

export async function getPendingPhotos() {
  const db = await getDb();
  return db.getAllFromIndex('pendingPhotos', 'by_synced', 0);
}

export async function markPendingPhotoAsSynced(localId) {
  const db = await getDb();
  const record = await db.get('pendingPhotos', localId);
  if (record) {
    record.synced = 1;
    record.synced_at = new Date().toISOString();
    await db.put('pendingPhotos', record);
  }
}

export async function clearSyncedPhotos() {
  const db = await getDb();
  const all = await db.getAll('pendingPhotos');
  const tx = db.transaction('pendingPhotos', 'readwrite');
  for (const r of all) {
    if (r.synced) await tx.store.delete(r.localId);
  }
  await tx.done;
}

export default {
  savePlants, getPlants, getPlant,
  saveMeters, getMetersByPlant,
  saveReadingHistory, getReadingHistoryByMeter,
  savePendingReading, getPendingReadings, getAllPendingReadings,
  markPendingAsSynced, deletePendingReading, clearSyncedReadings,
  saveNotes, getNotesByPlant,
  setSyncMeta, getSyncMeta,
  syncFromServer, getLastSyncTime,
  clearAllData,
  savePendingPhoto, getPendingPhotos, markPendingPhotoAsSynced, clearSyncedPhotos
};
